import { TrendChartStyles, NoDataIndicator } from "../TrendChartStyles";
import { useTrendChartDragHandlers } from "../trendChartUtils";
import { TooBigToLoadIndicator } from "../../../SimpleComponents/TooBigToLoadIndicator";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS } from "../../../../utils/timeUtils";
import {
  useHideIfRangeTooLong,
  useGetPageFromPath,
  useFocusRange,
  useDashboardPageContext,
} from "../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useShouldHideProfitCategories,
  useTrendChartBarRangesForBarstacks,
} from "../../../../utils/customHooks/trendChartHooks";
import { useShowTrendLineOnThisPage } from "./TrendChartBar/TrendLine/trendLineUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useIsDarkMode } from "store/atoms";
import { TrendChartBars } from "./TrendChartBar/TrendChartBars";

const MAX_NUMBER_OF_TREND_CHART_BARS_ON_TIMELINE_PAGE = 4 * 24; // one-hour bars, 4 days max - this is arbitraty & applies to internal (fledge) features only

const TrendChartContent = ({
  isLoaderAnimationEnabled,
  isIE,
}: {
  isLoaderAnimationEnabled: boolean;
  isIE: boolean;
}) => {
  const { isGridLayoutPage, isTrendsPage } = useGetPageFromPath();
  const ckey = useCustomerKey();
  const { isFullDaySelected } = useDashboardPageContext();

  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();

  const shouldHideProfitCategories = useShouldHideProfitCategories();
  const focusRange = useFocusRange();

  const [{ trendChart: trendChartConfig }] = useFlightConfig();
  const dateRangeDuration = focusRange && focusRange.lt - focusRange.ge;

  // on trends page, hide if the trend bars' range exceeds the set focus range (shouldn't happen)
  const maxFocusRangeDuration =
    isTrendsPage && dateRangeDuration
      ? dateRangeDuration + 3 * ONE_DAY_OF_MS // add 3 days for short month exceptions like Feb -> Mar
      : MAX_NUMBER_OF_TREND_CHART_BARS_ON_TIMELINE_PAGE * ONE_HOUR_OF_MS;

  const { shouldHide: isOverMaxFocusRange, handleShowAnyway } =
    useHideIfRangeTooLong({
      maxFocusRangeDuration,
    });

  // mouse handlers:
  // on drag: scroll horizontally
  // on click: open PnlPopup
  // https://stackoverflow.com/questions/6042202/how-to-distinguish-mouse-click-and-drag

  const {
    handleMouseLeave,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    mouseMovedRef,
    overflowWrapperRef,
  } = useTrendChartDragHandlers();

  // don't show left axis if we're showing profit categories (it's sized for profits OR losses only)
  const shouldShowLeftAxis = trendChartConfig.hideProfitCategories;
  // only show right axis if trend line feature is allowed on this page
  const shouldShowRightAxis = useShowTrendLineOnThisPage();
  // don't show horizontal target line if we're showing the target trend line instead

  const numBars = (trendChartBarsRanges || []).length;

  const isStorybook = useIsStorybook();

  const isTooBigToLoad =
    // never hide in storybook
    !isStorybook &&
    // hide if focus range is too big
    isOverMaxFocusRange &&
    !window.location.pathname.includes("fromSnapshots");

  const showNoDataIndicator =
    isTrendsPage &&
    (numBars === 0 ||
      trendChartBarsRanges.reduce((acc, { ge, lt }) => acc + (lt - ge), 0) ===
        0);

  const isFullPageStory = ckey.includes("_page_story"); // having shippable timezone issues only in the full-page stories (Pages.stories.tsx)

  const [isDarkMode] = useIsDarkMode();

  return (
    <div
      ref={overflowWrapperRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {isTooBigToLoad ? (
        <TooBigToLoadIndicator
          name={"trend chart"}
          handleTryToLoad={handleShowAnyway}
          maxFocusRangeDuration={maxFocusRangeDuration}
        />
      ) : (
        <TrendChartStyles
          $shouldShowLeftAxis={shouldShowLeftAxis}
          $shouldShowRightAxis={shouldShowRightAxis}
          $isGridLayoutPage={isGridLayoutPage}
          $shouldHideProfitCategories={shouldHideProfitCategories}
          className={`trendChartStyles ${isIE ? " internetExplorer" : ""}${
            showNoDataIndicator ? " noRollups" : ""
          }${isFullDaySelected ? " isFullDayMode" : ""}${
            trendChartBarsRanges.length > 12 ? " manyBars" : ""
          }${trendChartBarsRanges.length > 20 ? " flexWrapBars" : ""}${
            isTrendsPage ? " isTrendsPage" : ""
          }`}
          data-focus-range-ge={
            isStorybook || isFullPageStory ? null : focusRange?.ge
          }
          data-focus-range-lt={
            isStorybook || isFullPageStory ? null : focusRange?.lt
          }
          $isDarkMode={isDarkMode}
        >
          {showNoDataIndicator ? (
            <NoDataIndicator />
          ) : (
            <TrendChartBars
              {...{
                isClickDisabled: mouseMovedRef.current,
                isLoaderAnimationEnabled,
              }}
            />
          )}
        </TrendChartStyles>
      )}
    </div>
  );
};

export default TrendChartContent;
